import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  Chip,
  Stack,
  Grow,
  Button,
  Slider,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar
} from 'recharts';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import { talepTahminiData } from '../../data/mockData';
import AnimatedNumber from '../../components/common/AnimatedNumber';
import ModelSelectionDialog from '../../components/dialogs/ModelSelectionDialog'; // ModelSelectionDialog importunu ekle

// Remove unused imports
// import ModelComparisonChart from '../../components/charts/ModelComparisonChart';
// import HelpIcon from '@mui/icons-material/Help';
// import SwapVertIcon from '@mui/icons-material/SwapVert';
// import MoreVertIcon from '@mui/icons-material/MoreVert';
// import ParametricControls from '../../components/common/ParametricControls';

// Daha fazla mock veri ekleyelim
const extendedData = {
  weekly: [
    { period: 'Hafta 1', gerçek: 985, tahmin: 1000 },
    { period: 'Hafta 2', gerçek: 1120, tahmin: 1050 },
    { period: 'Hafta 3', gerçek: 1167, tahmin: 1200 },
    { period: 'Hafta 4', gerçek: 1089, tahmin: 1100 },
    { period: 'Hafta 5', gerçek: 1256, tahmin: 1300 },
    { period: 'Hafta 6', gerçek: 1490, tahmin: 1450 },
    { period: 'Hafta 7', gerçek: 1350, tahmin: 1400 },
    { period: 'Hafta 8', gerçek: 1480, tahmin: 1500 }
  ],
  monthly: [
    { period: 'Ocak', gerçek: 4000, tahmin: 4100 },
    { period: 'Şubat', gerçek: 3000, tahmin: 3200 },
    { period: 'Mart', gerçek: 2000, tahmin: 2100 },
    { period: 'Nisan', gerçek: 2780, tahmin: 2700 },
    { period: 'Mayıs', gerçek: 1890, tahmin: 1950 },
    { period: 'Haziran', gerçek: 2390, tahmin: 2400 }
  ],
  quarterly: [
    { period: 'Q1', gerçek: 9000, tahmin: 9200 },
    { period: 'Q2', gerçek: 7060, tahmin: 6950 },
    { period: 'Q3', gerçek: 8790, tahmin: 9000 },
    { period: 'Q4', gerçek: 9490, tahmin: 9600 }
  ],
  // Sektörlere göre veri
  sectors: {
    retail: {
      accuracyMetrics: { mape: '3.8%', rmse: '112.3', accuracyRate: '96.2%' },
      businessImpact: { stockReduction: '22%', serviceLevel: '99.5%', costSavings: '₺620,000' }
    },
    manufacturing: {
      accuracyMetrics: { mape: '4.5%', rmse: '130.8', accuracyRate: '95.5%' },
      businessImpact: { stockReduction: '19%', serviceLevel: '98.9%', costSavings: '₺580,000' }
    },
    finance: {
      accuracyMetrics: { mape: '3.2%', rmse: '95.5', accuracyRate: '96.8%' },
      businessImpact: { stockReduction: '15%', serviceLevel: '99.6%', costSavings: '₺720,000' }
    },
    telecom: {
      accuracyMetrics: { mape: '5.1%', rmse: '145.2', accuracyRate: '94.9%' },
      businessImpact: { stockReduction: '17%', serviceLevel: '98.7%', costSavings: '₺490,000' }
    },
    healthcare: {
      accuracyMetrics: { mape: '3.9%', rmse: '118.6', accuracyRate: '96.1%' },
      businessImpact: { stockReduction: '16%', serviceLevel: '99.3%', costSavings: '₺540,000' }
    }
  },
  products: [
    { id: 1, name: 'Ürün A', forecastAccuracy: 96.5 },
    { id: 2, name: 'Ürün B', forecastAccuracy: 94.2 },
    { id: 3, name: 'Ürün C', forecastAccuracy: 97.8 },
    { id: 4, name: 'Ürün D', forecastAccuracy: 93.1 },
    { id: 5, name: 'Ürün E', forecastAccuracy: 95.6 }
  ],
  // Günlük veri ekleyelim - son 30 gün
  daily: Array.from({ length: 30 }, (_, i) => {
    const day = new Date();
    day.setDate(day.getDate() - (30 - i));
    const dayStr = day.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
    
    // Gerçekçi veri oluşturma - hafta sonları daha düşük değerler
    const isWeekend = [0, 6].includes(day.getDay());
    const baseDemand = isWeekend ? 300 + Math.random() * 150 : 450 + Math.random() * 200;
    
    // Mevsimsel etki
    const seasonalFactor = 1 + 0.2 * Math.sin(i / 30 * Math.PI * 2);
    
    // Random noise
    const noise = (Math.random() - 0.5) * 50;
    
    const realized = Math.round(baseDemand * seasonalFactor + noise);
    
    // Tahmine gerçeğe yakın bir değer + bias
    const forecast = Math.round(realized * (1 + (Math.random() - 0.5) * 0.15));
    
    return {
      period: dayStr,
      gerçek: realized,
      tahmin: forecast
    };
  }),

  // Saatlik veriler - son 24 saat
  hourly: Array.from({ length: 24 }, (_, i) => {
    const hour = i;
    
    // İş saatlerinde daha yüksek talep
    const isBusinessHour = hour >= 8 && hour <= 17;
    const baseDemand = isBusinessHour ? 150 + Math.random() * 100 : 80 + Math.random() * 50;
    
    // Öğle ve akşam saatlerinde artış
    let peakFactor = 1.0;
    if (hour === 12 || hour === 13) peakFactor = 1.3; // Öğle peak
    if (hour >= 17 && hour <= 19) peakFactor = 1.4;   // Akşam peak
    
    const realized = Math.round(baseDemand * peakFactor);
    const forecast = Math.round(realized * (1 + (Math.random() - 0.5) * 0.2));
    
    return {
      period: `${hour}:00`,
      gerçek: realized,
      tahmin: forecast
    };
  }),
  
  // Farklı modellerin performans karşılaştırması
  models: {
    "ARIMA": { mape: 5.2, rmse: 134.8, accuracyRate: 94.8, trainingTime: "2.5dk", complexity: "Düşük" },
    "Prophet": { mape: 4.8, rmse: 128.3, accuracyRate: 95.2, trainingTime: "4.2dk", complexity: "Orta" },
    "LSTM": { mape: 3.8, rmse: 112.4, accuracyRate: 96.2, trainingTime: "18.5dk", complexity: "Yüksek" },
    "RandomForest": { mape: 4.3, rmse: 118.9, accuracyRate: 95.7, trainingTime: "5.8dk", complexity: "Orta" },
    "XGBoost": { mape: 3.9, rmse: 115.6, accuracyRate: 96.1, trainingTime: "7.3dk", complexity: "Yüksek" }
  },
  
  // Ürün kategorilerine göre tahmin performansı
  categories: {
    "Gıda": { mape: 3.8, rmse: 98.5, accuracyRate: 96.2 },
    "Elektronik": { mape: 5.7, rmse: 142.3, accuracyRate: 94.3 },
    "Giyim": { mape: 4.2, rmse: 110.9, accuracyRate: 95.8 },
    "Kozmetik": { mape: 6.1, rmse: 156.8, accuracyRate: 93.9 },
    "Ev Aletleri": { mape: 4.9, rmse: 124.7, accuracyRate: 95.1 }
  },
  
  // Model eğitim geçmişi
  trainingHistory: [
    { epoch: 1, loss: 0.25, val_loss: 0.27 },
    { epoch: 2, loss: 0.18, val_loss: 0.19 },
    { epoch: 3, loss: 0.14, val_loss: 0.15 },
    { epoch: 4, loss: 0.10, val_loss: 0.12 },
    { epoch: 5, loss: 0.08, val_loss: 0.10 },
    { epoch: 6, loss: 0.07, val_loss: 0.09 },
    { epoch: 7, loss: 0.06, val_loss: 0.08 },
    { epoch: 8, loss: 0.05, val_loss: 0.08 },
    { epoch: 9, loss: 0.05, val_loss: 0.07 },
    { epoch: 10, loss: 0.04, val_loss: 0.07 }
  ],
  
  // Anomali tespiti
  anomalies: [
    { date: '15.09.2023', value: 1842, expected: 1250, deviation: '+47%', reason: 'Kampanya Başlangıcı' },
    { date: '23.10.2023', value: 324, expected: 1180, deviation: '-72%', reason: 'Tedarik Sorunu' },
    { date: '05.11.2023', value: 2105, expected: 1320, deviation: '+59%', reason: 'Black Friday' }
  ]
};

const TalepTahmini = () => {
  const { accuracyMetrics: defaultMetrics, businessImpact: defaultImpact, caseStudies } = talepTahminiData;
  
  const [tabValue, setTabValue] = useState(0);
  const [chartType, setChartType] = useState('line');
  const [params, setParams] = useState({
    sector: 'all',
    dateRange: 'last6months',
    granularity: 'monthly',
    confidenceInterval: 90
  });
  
  const [chartData, setChartData] = useState(extendedData.monthly);
  const [accuracyMetrics, setAccuracyMetrics] = useState(defaultMetrics);
  const [businessImpact, setBusinessImpact] = useState(defaultImpact);
  const [isTraining, setIsTraining] = useState(false);
  const [selectedModel, setSelectedModel] = useState('LSTM');
  const [modelSelectionOpen, setModelSelectionOpen] = useState(false);
  const [anomalyDetection, setAnomalyDetection] = useState(true);
  const [forecastHorizon, setForecastHorizon] = useState(30);
  const [selectedFeatures, setSelectedFeatures] = useState({
    historicalSales: true, // Bu temel feature, her zaman seçili ve değiştirilemez
    price: true, // Bu temel feature, her zaman seçili ve değiştirilemez
    promotions: true, // Bu temel feature, her zaman seçili ve değiştirilemez
    socialMedia: false,
    economicIndicators: false,
    weather: false,
    location: false,
    webTraffic: false
  });

  // State eklemeleri
  const [baseFeatures] = useState({
    historicalSales: true,
    price: true,
    promotions: true,
    stockLevels: true,
    seasonality: true,
    specialDays: true
  });

  const [additionalFeatures, setAdditionalFeatures] = useState({
    socialMedia: false,
    economicIndicators: false,
    weather: false,
    location: false,
    webTraffic: false
  });

  // Feature açıklamaları
  const featureDescriptions = {
    base: {
      historicalSales: {
        title: 'Tarihsel Satış Verileri',
        description: 'Geçmiş dönemlerdeki satış miktarları ve zaman serisi analizi'
      },
      price: {
        title: 'Satış Fiyatı',
        description: 'Ürünün satış fiyatı ve fiyat değişim geçmişi'
      },
      promotions: {
        title: 'Promosyonlar',
        description: 'İndirim kampanyaları ve özel teklifler'
      },
      stockLevels: {
        title: 'Stok Seviyeleri',
        description: 'Mevcut stok durumu ve stok geçmişi'
      },
      seasonality: {
        title: 'Mevsimsellik',
        description: 'Yılın hangi dönemi (Yaz, kış, vs.)'
      },
      specialDays: {
        title: 'Özel Günler',
        description: 'Tatil, bayram, okul açılışı gibi özel dönemler'
      }
    },
    additional: {
      socialMedia: {
        title: 'Sosyal Medya Duyarlılığı',
        description: 'Ürün/marka hakkındaki sosyal medya yorumları ve trend analizleri',
        impact: '5-15%'
      },
      economicIndicators: {
        title: 'Ekonomik Göstergeler',
        description: 'Enflasyon, TÜFE, tüketici güven endeksi',
        impact: '10-20%'
      },
      weather: {
        title: 'Hava Durumu',
        description: 'Hava koşullarının talep üzerindeki etkisi',
        impact: '3-12%'
      },
      location: {
        title: 'Mağaza Lokasyonu',
        description: 'Bölgesel özellikler ve demografik veriler',
        impact: '8-25%'
      },
      webTraffic: {
        title: 'Web Trafiği',
        description: 'Ürün sayfası ziyaretleri ve etkileşim metrikleri',
        impact: '5-18%'
      }
    }
  };

  // Feature seçim işleyicisi
  const handleFeatureToggle = (feature) => {
    setAdditionalFeatures(prev => ({
      ...prev,
      [feature]: !prev[feature]
    }));
  };

  // Parametreler değiştiğinde grafik verilerini güncelle
  useEffect(() => {
    // Granülasyona göre veri değişimi
    if (params.granularity === 'weekly') {
      setChartData(extendedData.weekly);
    } else if (params.granularity === 'monthly') {
      setChartData(extendedData.monthly);
    } else if (params.granularity === 'quarterly') {
      setChartData(extendedData.quarterly);
    } else if (params.granularity === 'daily') {
      setChartData(extendedData.daily);
    } else if (params.granularity === 'hourly') {
      setChartData(extendedData.hourly);
    }
    
    // Sektöre göre metrik değişimi
    if (params.sector !== 'all' && extendedData.sectors[params.sector]) {
      const sectorData = extendedData.sectors[params.sector];
      setAccuracyMetrics(sectorData.accuracyMetrics);
      setBusinessImpact(sectorData.businessImpact);
    } else {
      setAccuracyMetrics(defaultMetrics);
      setBusinessImpact(defaultImpact);
    }
  }, [params, defaultMetrics, defaultImpact]);

  const handleParamsChange = (newParams) => {
    setParams(newParams);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleChartTypeChange = (event) => {
    setChartType(event.target.value);
  };

  // Güven aralığını hesaplamak için
  const getConfidenceBands = (item) => {
    const confidence = params.confidenceInterval / 100;
    const range = item.tahmin * (1 - confidence) * 2;
    return {
      upper: item.tahmin + (range / 2),
      lower: item.tahmin - (range / 2)
    };
  };

  // ChartData'yı genişletip güven aralıklarını ekleyelim
  const enhancedChartData = chartData.map(item => {
    const bands = getConfidenceBands(item);
    return {
      ...item,
      upper: bands.upper,
      lower: bands.lower
    };
  });

  // Simüle edilmiş eğitim durumu
  const startModelTraining = () => {
    setIsTraining(true);
    // Gerçekçi bir eğitim süresi simülasyonu
    setTimeout(() => {
      setIsTraining(false);
      // Eğitim tamamlandığında metrikleri güncelle
      setAccuracyMetrics(extendedData.models[selectedModel]);
    }, 3000);
  };

  // Model seçim dialog'unu aç/kapat
  const handleModelDialogOpen = () => {
    setModelSelectionOpen(true);
  };
  
  const handleModelDialogClose = (model) => {
    if (model) {
      setSelectedModel(model);
      // Yeni modelin metriklerini uygula
      setAccuracyMetrics(extendedData.models[model]);
    }
    setModelSelectionOpen(false);
  };

  // İleri tahmin verisi oluştur
  const generateForecastData = () => {
    if (chartData.length === 0) return [];
    
    const lastPeriod = chartData[chartData.length - 1].period;
    const lastValue = chartData[chartData.length - 1].tahmin;
    
    // Granüliteye göre bir sonraki dönem formatını belirle
    let nextPeriodFunc;
    
    switch(params.granularity) {
      case 'hourly':
        nextPeriodFunc = (period, i) => {
          const hour = parseInt(period.split(':')[0]);
          return `${(hour + i) % 24}:00`;
        };
        break;
      case 'daily':
        nextPeriodFunc = (period, i) => {
          const date = new Date();
          date.setDate(date.getDate() + i);
          return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
        };
        break;
      case 'weekly':
        nextPeriodFunc = (period, i) => `Hafta ${parseInt(period.split(' ')[1]) + i}`;
        break;
      case 'monthly':
        nextPeriodFunc = (period, i) => {
          const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
          const currentMonthIndex = months.indexOf(period);
          return months[(currentMonthIndex + i) % 12];
        };
        break;
      case 'quarterly':
        nextPeriodFunc = (period, i) => `Q${parseInt(period.substring(1)) + i}`;
        break;
      default:
        nextPeriodFunc = (period, i) => `Period ${i}`;
    }
    
    // Trend ve mevsimsellik ekleyerek tahmini veriler oluştur
    return Array.from({ length: forecastHorizon }, (_, i) => {
      const trendFactor = 1 + (Math.random() * 0.02 - 0.005) * i; // Küçük bir trend
      const seasonalFactor = 1 + 0.15 * Math.sin((i / forecastHorizon) * Math.PI * 2); // Mevsimsel dalgalanma
      const randomNoise = (Math.random() - 0.5) * 0.1; // Rastgele gürültü
      
      // Model doğruluğuna göre tahmin kalitesini ayarla
      const modelAccuracy = parseInt(accuracyMetrics.accuracyRate) / 100;
      const errorFactor = 1 - modelAccuracy;
      const errorComponent = (Math.random() - 0.5) * errorFactor * 0.5;
      
      const forecastValue = lastValue * trendFactor * seasonalFactor * (1 + randomNoise + errorComponent);
      
      const period = nextPeriodFunc(lastPeriod, i + 1);
      
      const bands = getConfidenceBands({ tahmin: forecastValue });
      
      return {
        period,
        tahmin: Math.round(forecastValue),
        upper: Math.round(bands.upper),
        lower: Math.round(bands.lower),
        forecast: 1 // Bu bir tahmin verisi olduğunu belirtmek için
      };
    });
  };

  // Mevcut ve gelecek verilerini birleştir
  const combinedChartData = anomalyDetection 
    ? [...enhancedChartData, ...generateForecastData()]
    : enhancedChartData;
    
  // Anomali tespiti için filtrelemeyi kaldıralım
  /* const anomalies = enhancedChartData.filter(item => {
    if (!item.gerçek) return false;
    const deviation = Math.abs(item.gerçek - item.tahmin) / item.tahmin;
    return deviation > 0.25; // %25'ten fazla sapma varsa anomali olarak işaretle
  }); */

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }} className="slide-in-left">
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Talep Tahmini
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" paragraph>
          Makine öğrenmesi algoritmaları ile gelecekteki talepleri öngörün ve iş süreçlerinizi optimize edin.
        </Typography>
        <Divider sx={{ my: 3 }} />
      </Box>

      {/* Model Seçimi ve Parametrik Kontroller */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={9}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" component="h2">Model Parametreleri</Typography>
                
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    startIcon={<ModelTrainingIcon />}
                    onClick={handleModelDialogOpen}
                    size="small"
                  >
                    Model: {selectedModel}
                  </Button>
                  
                  <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<AutorenewIcon />}
                    onClick={startModelTraining}
                    disabled={isTraining}
                    size="small"
                  >
                    {isTraining ? 'Eğitiliyor...' : 'Modeli Eğit'}
                  </Button>
                </Box>
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Veri Granülasyonu</InputLabel>
                    <Select
                      value={params.granularity}
                      label="Veri Granülasyonu"
                      onChange={(e) => handleParamsChange({ granularity: e.target.value })}
                    >
                      <MenuItem value="hourly">Saatlik</MenuItem>
                      <MenuItem value="daily">Günlük</MenuItem>
                      <MenuItem value="weekly">Haftalık</MenuItem>
                      <MenuItem value="monthly">Aylık</MenuItem>
                      <MenuItem value="quarterly">Çeyreklik</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Sektör</InputLabel>
                    <Select
                      value={params.sector}
                      label="Sektör"
                      onChange={(e) => handleParamsChange({ sector: e.target.value })}
                    >
                      <MenuItem value="all">Tüm Sektörler</MenuItem>
                      <MenuItem value="retail">Perakende</MenuItem>
                      <MenuItem value="manufacturing">Üretim</MenuItem>
                      <MenuItem value="finance">Finans</MenuItem>
                      <MenuItem value="telecom">Telekom</MenuItem>
                      <MenuItem value="healthcare">Sağlık</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Ürün Kategorisi</InputLabel>
                    <Select
                      value={params.productCategory}
                      label="Ürün Kategorisi"
                      onChange={(e) => handleParamsChange({ productCategory: e.target.value })}
                    >
                      <MenuItem value="all">Tüm Kategoriler</MenuItem>
                      <MenuItem value="Gıda">Gıda</MenuItem>
                      <MenuItem value="Elektronik">Elektronik</MenuItem>
                      <MenuItem value="Giyim">Giyim</MenuItem>
                      <MenuItem value="Kozmetik">Kozmetik</MenuItem>
                      <MenuItem value="Ev Aletleri">Ev Aletleri</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Güven Aralığı: %{params.confidenceInterval}
                </Typography>
                <Slider
                  value={params.confidenceInterval}
                  onChange={(e, newValue) => handleParamsChange({ confidenceInterval: newValue })}
                  valueLabelDisplay="auto"
                  step={5}
                  marks
                  min={60}
                  max={95}
                />
              </Box>
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <FormControl component="fieldset" sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>İleri Tahmin Görüntüle:</Typography>
                  <Switch
                    checked={anomalyDetection}
                    onChange={(e) => setAnomalyDetection(e.target.checked)}
                    name="anomalyDetection"
                  />
                </FormControl>
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>Tahmin Süresi:</Typography>
                  <Select
                    value={forecastHorizon}
                    size="small"
                    onChange={(e) => setForecastHorizon(e.target.value)}
                  >
                    <MenuItem value={7}>7 dönem</MenuItem>
                    <MenuItem value={14}>14 dönem</MenuItem>
                    <MenuItem value={30}>30 dönem</MenuItem>
                    <MenuItem value={60}>60 dönem</MenuItem>
                  </Select>
                </Box>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" component="h2" gutterBottom>Model Doğruluğu</Typography>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h3" color="primary" sx={{ fontWeight: 'bold' }}>
                  {accuracyMetrics.accuracyRate}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Genel Doğruluk Oranı
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>MAPE:</span>
                <span>{accuracyMetrics.mape}%</span>
              </Typography>
              <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>RMSE:</span>
                <span>{accuracyMetrics.rmse}</span>
              </Typography>
              <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Eğitim Süresi:</span>
                <span>{accuracyMetrics.trainingTime || 'N/A'}</span>
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Feature Seçim Kartları */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Temel Özellikler */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Temel Tahmin Özellikleri
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Talep tahmininde kullanılan temel özellikler. Bu özellikler her zaman aktiftir.
            </Typography>
            
            <Stack spacing={2}>
              {Object.entries(featureDescriptions.base).map(([key, feature]) => (
                <Box
                  key={key}
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    bgcolor: 'primary.main',
                    color: 'white',
                    opacity: 0.9
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    {feature.title}
                  </Typography>
                  <Typography variant="caption">
                    {feature.description}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>

        {/* Ek Özellikler */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Ek Tahmin Özellikleri
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Tahmin doğruluğunu artırmak için eklenebilecek ilave özellikler.
            </Typography>
            
            <Stack spacing={2}>
              {Object.entries(featureDescriptions.additional).map(([key, feature]) => (
                <Box
                  key={key}
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    bgcolor: additionalFeatures[key] ? 'rgba(0, 141, 151, 0.1)' : 'transparent',
                    border: '1px solid',
                    borderColor: additionalFeatures[key] ? 'primary.main' : 'divider'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {feature.title}
                    </Typography>
                    <Switch
                      checked={additionalFeatures[key]}
                      onChange={() => handleFeatureToggle(key)}
                      color="primary"
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {feature.description}
                  </Typography>
                  {additionalFeatures[key] && (
                    <Chip
                      label={`Tahmini Etki: ${feature.impact}`}
                      size="small"
                      color="primary"
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ mb: 3 }}
        className="fade-in"
      >
        <Tab label="Tahmin Analizi" />
        <Tab label="Model Karşılaştırma" />
        <Tab label="İş Etkisi" />
      </Tabs>

      <Box className="fade-in" sx={{ display: tabValue === 0 ? 'block' : 'none' }}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 3, 
                borderRadius: 2,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '4px',
                  background: 'linear-gradient(90deg, #3f51b5, #f50057)',
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>Tahmin Performansı</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {params.sector !== 'all' 
                      ? `${params.granularity === 'hourly' ? 'Saatlik' : params.granularity === 'daily' ? 'Günlük' : params.granularity === 'weekly' ? 'Haftalık' : params.granularity === 'monthly' ? 'Aylık' : 'Çeyreklik'} tahmin performansı - ${params.sector === 'retail' ? 'Perakende' : params.sector === 'manufacturing' ? 'Üretim' : params.sector === 'finance' ? 'Finans' : params.sector === 'telecom' ? 'Telekom' : 'Sağlık'} sektörü`
                      : `${params.granularity === 'hourly' ? 'Saatlik' : params.granularity === 'daily' ? 'Günlük' : params.granularity === 'weekly' ? 'Haftalık' : params.granularity === 'monthly' ? 'Aylık' : 'Çeyreklik'} tahmin performansı - Tüm sektörler`}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="chart-type-label">Grafik Türü</InputLabel>
                    <Select
                      labelId="chart-type-label"
                      id="chart-type"
                      value={chartType}
                      onChange={handleChartTypeChange}
                      label="Grafik Türü"
                    >
                      <MenuItem value="line">Çizgi</MenuItem>
                      <MenuItem value="area">Alan</MenuItem>
                      <MenuItem value="bar">Çubuk</MenuItem>
                    </Select>
                  </FormControl>
                  <Tooltip title="Veriyi İndir">
                    <IconButton size="small" sx={{ border: '1px solid #ddd' }}>
                      <FileDownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Yenile">
                    <IconButton size="small" sx={{ border: '1px solid #ddd' }}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
              <Box sx={{ height: 400 }} className="scale-in">
                {chartType === 'line' && (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={combinedChartData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="period" 
                        angle={-45} 
                        textAnchor="end" 
                        tickMargin={10} 
                        height={50} 
                      />
                      <YAxis />
                      <RechartsTooltip 
                        formatter={(value, name) => {
                          if (name === 'gerçek') return ['Gerçekleşen', value];
                          if (name === 'tahmin') return ['Tahmin', value];
                          if (name === 'upper') return ['Üst Sınır', value];
                          if (name === 'lower') return ['Alt Sınır', value];
                          return [name, value];
                        }}
                      />
                      <Legend />
                      
                      {/* Güven aralığı alanı */}
                      <Area 
                        type="monotone" 
                        dataKey="upper" 
                        stroke="transparent" 
                        fill="#82ca9d" 
                        fillOpacity={0.1} 
                        name="Güven Aralığı"
                        legendType="none"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="lower" 
                        stroke="transparent" 
                        fill="#82ca9d" 
                        fillOpacity={0.1} 
                        name="Güven Aralığı"
                        legendType="none" 
                      />
                      
                      {/* Gerçek değerler - sadece eldeki gerçek veriler için */}
                      <Line 
                        type="monotone" 
                        dataKey="gerçek" 
                        name="Gerçekleşen" 
                        stroke="#8884d8" 
                        strokeWidth={2}
                        activeDot={{ r: 8 }} 
                      />
                      <Line 
                        type="monotone" 
                        dataKey="tahmin" 
                        name="Tahmin" 
                        stroke="#82ca9d" 
                        strokeWidth={2}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="upper" 
                        name="Üst Güven Sınırı" 
                        stroke="#82ca9d" 
                        strokeWidth={1}
                        strokeDasharray="5 5"
                        dot={false}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="lower" 
                        name="Alt Güven Sınırı" 
                        stroke="#82ca9d" 
                        strokeWidth={1}
                        strokeDasharray="5 5"
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
                
                {chartType === 'area' && (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={enhancedChartData}
                      margin={{
                        top: 20, right: 30, left: 20, bottom: 10,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <defs>
                        <linearGradient id="colorGercek" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#8884d8" stopOpacity={0.1}/>
                        </linearGradient>
                        <linearGradient id="colorTahmin" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#82ca9d" stopOpacity={0.1}/>
                        </linearGradient>
                      </defs>
                      <Area 
                        type="monotone" 
                        dataKey="gerçek" 
                        name="Gerçekleşen" 
                        stroke="#8884d8"
                        fillOpacity={1}
                        fill="url(#colorGercek)"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="tahmin" 
                        name="Tahmin" 
                        stroke="#82ca9d" 
                        fillOpacity={1}
                        fill="url(#colorTahmin)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
                
                {chartType === 'bar' && (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={enhancedChartData}
                      margin={{
                        top: 20, right: 30, left: 20, bottom: 10,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Bar dataKey="gerçek" name="Gerçekleşen" fill="#8884d8" />
                      <Bar dataKey="tahmin" name="Tahmin" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Box>
              
              <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: 'flex-end' }}>
                <Chip 
                  icon={<InfoOutlinedIcon />}
                  label={`MAPE: ${accuracyMetrics.mape}`}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
                <Chip 
                  icon={<InfoOutlinedIcon />}
                  label={`Güven Aralığı: ${params.confidenceInterval}%`}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Grow in={true} timeout={1000}>
              <Card sx={{ 
                height: '100%',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                }
              }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Doğruluk Metrikleri</Typography>
                  <Grid container spacing={3} sx={{ mt: 2 }}>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <AnimatedNumber 
                          value={parseFloat(accuracyMetrics.mape)} 
                          duration={2000} 
                          suffix="%" 
                          variant="h4" 
                          color="primary.main" 
                          sx={{ fontWeight: 'bold' }}
                        />
                        <Typography variant="body2" color="text.secondary">MAPE</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <AnimatedNumber 
                          value={parseFloat(accuracyMetrics.rmse)} 
                          duration={2000}
                          variant="h4" 
                          color="primary.main" 
                          sx={{ fontWeight: 'bold' }}
                        />
                        <Typography variant="body2" color="text.secondary">RMSE</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <AnimatedNumber 
                          value={parseFloat(accuracyMetrics.accuracyRate)} 
                          duration={2000} 
                          suffix="%" 
                          variant="h4" 
                          color="primary.main" 
                          sx={{ fontWeight: 'bold' }}
                        />
                        <Typography variant="body2" color="text.secondary">Doğruluk Oranı</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grow>
          </Grid>

          <Grid item xs={12} md={6}>
            <Grow in={true} timeout={1000}>
              <Card sx={{ 
                height: '100%',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                }
              }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>İş Etkisi</Typography>
                  <Grid container spacing={3} sx={{ mt: 2 }}>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <AnimatedNumber 
                          value={parseFloat(businessImpact.stockReduction)} 
                          duration={2000} 
                          suffix="%" 
                          variant="h4" 
                          color="primary.main" 
                          sx={{ fontWeight: 'bold' }}
                        />
                        <Typography variant="body2" color="text.secondary">Stok Azalması</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <AnimatedNumber 
                          value={parseFloat(businessImpact.serviceLevel)} 
                          duration={2000} 
                          suffix="%" 
                          variant="h4" 
                          color="primary.main" 
                          sx={{ fontWeight: 'bold' }}
                        />
                        <Typography variant="body2" color="text.secondary">Hizmet Seviyesi</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <AnimatedNumber 
                          value={parseFloat(businessImpact.costSavings.replace('₺', '').replace(',', ''))} 
                          duration={2000} 
                          prefix="₺" 
                          variant="h4" 
                          color="primary.main" 
                          sx={{ fontWeight: 'bold' }}
                        />
                        <Typography variant="body2" color="text.secondary">Maliyet Tasarrufu</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grow>
          </Grid>
        </Grid>
      </Box>

      <Box className="fade-in" sx={{ display: tabValue === 1 ? 'block' : 'none' }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Doğruluk Metrikleri</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2">MAPE:</Typography>
                  <Typography variant="body1" fontWeight="bold">{accuracyMetrics.mape}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2">RMSE:</Typography>
                  <Typography variant="body1" fontWeight="bold">{accuracyMetrics.rmse}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Doğruluk Oranı:</Typography>
                  <Typography variant="body1" fontWeight="bold">{accuracyMetrics.accuracyRate}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>İş Etkisi</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2">Stok Azalması:</Typography>
                  <Typography variant="body1" fontWeight="bold">{businessImpact.stockReduction}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2">Hizmet Seviyesi:</Typography>
                  <Typography variant="body1" fontWeight="bold">{businessImpact.serviceLevel}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Maliyet Tasarrufu:</Typography>
                  <Typography variant="body1" fontWeight="bold">{businessImpact.costSavings}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Box className="fade-in" sx={{ display: tabValue === 2 ? 'block' : 'none' }}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Başarı Hikayeleri</Typography>
                {caseStudies.map((study, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">{study.company}</Typography>
                    <Typography variant="body2" color="text.secondary">Zorluk: {study.challenge}</Typography>
                    <Typography variant="body2" color="text.secondary">Çözüm: {study.solution}</Typography>
                    <Typography variant="body2" color="text.secondary">Sonuçlar: {study.results}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      <ModelSelectionDialog 
        open={modelSelectionOpen}
        onClose={handleModelDialogClose}
        currentModel={selectedModel}
      />
    </Container>
  );
};

export default TalepTahmini;

